import React, { Component, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Worker, Viewer, SpecialZoomLevel, LoadError } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import Spinner from './TailwindComponents/Spinner';

interface SafePdfViewerProps {
  pdfUrl: string;
  filename?: string;
  /** Reading zoom factor from the viewer chrome, 1 = 100%. */
  zoom?: number;
  /**
   * Text to highlight and scroll to (F-REVIEW for PDFs): tiers of patterns,
   * tried in order until one matches. `token` re-runs it - a new finding
   * selected, or the same one clicked again.
   */
  highlight?: { tiers: RegExp[][]; token: string } | null;
}

/**
 * A corrupt / undecryptable document can make pdf.js throw DURING RENDER, which
 * the viewer's own `renderError` does NOT catch - that only handles the
 * document-LOAD task. Such a throw would otherwise escape to the global app
 * ErrorBoundary and take down (and reload-loop) the whole view. This local
 * boundary contains it and shows an inline message instead. `resetKey` (the
 * pdfUrl) lets it recover when the user opens a different, valid document.
 *
 * It wraps the WHOLE viewer, not just `<Viewer>`: a boundary only catches
 * throws from its descendants, and the B1 crash came from this component's own
 * hooks, one level above where the boundary used to sit.
 */
class PdfRenderBoundary extends Component<
  { resetKey: string; fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error) {
    console.error('[SafePdfViewer] Render error contained by local boundary:', error);
  }
  componentDidUpdate(prev: { resetKey: string }) {
    if (prev.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

/**
 * WHY THE SCALE IS PINNED (files-12).
 *
 * With `defaultScale` left as a SpecialZoomLevel, react-pdf-viewer keeps that
 * level alive: every container resize re-fits the page to whatever width the
 * frame happens to have that moment (`keepSpecialZoomLevelRef`). Collapsing
 * the sidebar or toggling expanded view therefore re-laid-out the sheet
 * repeatedly and it appeared to swing left and right - and any zoom the reader
 * had chosen was silently overwritten.
 *
 * So: fit to width ONCE, then convert that fit into a fixed number by calling
 * `zoomTo` with it, which clears the library's "keep this level" flag. From
 * then on the page keeps its size and a wider window adds margin instead of
 * re-scaling. The zoom control multiplies that pinned base.
 */
const PdfViewerBody: React.FC<SafePdfViewerProps> = ({ pdfUrl, filename = 'document.pdf', zoom = 1, highlight = null }) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  /*
    `defaultLayoutPlugin()` IS A HOOK - it keeps its stores in React.useMemo /
    useState - so it must run unconditionally on every render, like any hook
    (B1). Wrapped in useMemo its hooks ran on mount only; the next render had
    fewer hooks, every later hook read the wrong slot, and React crashed on the
    zoom effect's missing deps ("reading 'length'"). The instance object is new
    each render but its stores are memoised inside, so state carries over.
  */
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const zoomToRef = useRef(defaultLayoutPluginInstance.toolbarPluginInstance.zoomPluginInstance.zoomTo);
  zoomToRef.current = defaultLayoutPluginInstance.toolbarPluginInstance.zoomPluginInstance.zoomTo;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef(defaultLayoutPluginInstance.toolbarPluginInstance.searchPluginInstance);
  searchRef.current = defaultLayoutPluginInstance.toolbarPluginInstance.searchPluginInstance;
  const [docLoaded, setDocLoaded] = useState(false);

  // The fit-to-width scale, resolved on load and never recomputed by a resize.
  const baseScaleRef = useRef<number | null>(null);
  const appliedZoomRef = useRef<number>(zoom);

  /*
    SAME ORIGIN, ALWAYS. This fetched the worker from a public npm CDN, which
    is two problems in a deployed environment: a Content-Security-Policy whose
    `script-src`/`worker-src` does not name that host blocks it outright, and a
    firm running PONS in the EU has just been made to depend on a third party
    to open its own document.

    `public/pdf.worker.min.js` is served by us, from our origin, and is kept in
    step with the installed `pdfjs-dist` by `scripts/sync-pdf-worker.js` —
    which exists because the committed copy had drifted to 4.8.69 against a
    3.11.174 API and pdf.js refused every document with a version mismatch.
  */
  const workerUrl = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

  // Mirrors the library's own PageWidth arithmetic (core: calculateScale).
  const SCROLL_BAR_WIDTH = 17;

  // Zooming is cosmetic: a viewer that is mid-teardown or not laid out yet
  // must never turn it into a crash.
  const applyScale = useCallback((scale: number) => {
    if (!Number.isFinite(scale) || scale <= 0) return;
    try {
      zoomToRef.current(scale);
    } catch (zoomError) {
      console.error('[SafePdfViewer] Could not apply zoom:', zoomError);
    }
  }, []);

  const handleDocumentLoad = useCallback(
    async (e: { doc: any }) => {
      setError(null);
      setDocLoaded(true);
      try {
        const page = await e.doc.getPage(1);
        const pageWidth = page.getViewport({ scale: 1 }).width;
        if (!pageWidth) return;
        requestAnimationFrame(() => {
          const pages = containerRef.current?.querySelector('.rpv-core__inner-pages') as HTMLElement | null;
          const available = pages?.clientWidth || containerRef.current?.clientWidth || 0;
          if (!available) return;
          const base = (available - SCROLL_BAR_WIDTH) / pageWidth;
          baseScaleRef.current = base;
          appliedZoomRef.current = zoom;
          // A NUMERIC scale - this is what clears the library's
          // "keep re-fitting to the container" flag.
          applyScale(base * zoom);
        });
      } catch (loadError) {
        console.error('[SafePdfViewer] Could not resolve page size:', loadError);
      }
    },
    [zoom, applyScale]
  );

  useEffect(() => {
    if (baseScaleRef.current === null || appliedZoomRef.current === zoom) return;
    appliedZoomRef.current = zoom;
    applyScale(baseScaleRef.current * zoom);
  }, [zoom, applyScale]);

  // A different document starts over. Only on a CHANGE: on mount this effect
  // runs after the viewer's first callbacks and would undo "loaded".
  const lastUrlRef = useRef(pdfUrl);
  useEffect(() => {
    if (lastUrlRef.current === pdfUrl) return;
    lastUrlRef.current = pdfUrl;
    baseScaleRef.current = null;
    setDocLoaded(false);
  }, [pdfUrl]);

  /*
    Highlight the change and go to it. The search plugin marks matches in the
    text layer; tiers go from exact (the changed words in their context) to
    loose (the clause opening) and stop at the first that hits. Like zoom,
    this is cosmetic - it must never take the viewer down.
  */
  const highlightToken = highlight?.token;
  useEffect(() => {
    if (!docLoaded || !highlight || highlight.tiers.length === 0) return undefined;
    let cancelled = false;
    (async () => {
      for (const tier of highlight.tiers) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const matches = await searchRef.current.highlight(tier);
          if (cancelled) return;
          if (matches && matches.length > 0) {
            searchRef.current.jumpToMatch(1);
            return;
          }
        } catch (highlightError) {
          console.error('[SafePdfViewer] Could not highlight:', highlightError);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docLoaded, highlightToken]);

  const renderErrorFallback = (error: LoadError) => {
    console.error('[SafePdfViewer] PDF Load Error:', error);
    return (
      <div className="flex items-center justify-center h-full bg-danger-50 dark:bg-danger-900/20">
        <div className="glass p-6 rounded-lg -danger-200 dark:-danger-800 max-w-sm text-center">
          <p className="text-danger-700 dark:text-danger-300 font-medium mb-2">{t('Failed to render PDF')}</p>
          <p className="text-sm text-danger-600 dark:text-danger-400">{error.message}</p>
          <p className="text-xs text-danger-500 dark:text-danger-500 mt-2">{t('Check browser console for details')}</p>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-danger-50 dark:bg-danger-900/20">
        <div className="glass p-6 rounded-lg -danger-200 dark:-danger-800 max-w-sm">
          <p className="text-danger-700 dark:text-danger-300 font-medium mb-2">{t('Unable to display PDF')}</p>
          <p className="text-sm text-danger-600 dark:text-danger-400 mb-3">{error}</p>
          <p className="text-xs text-danger-500 dark:text-danger-400 font-mono break-all">{filename}</p>
        </div>
      </div>
    );
  }

  /*
    ONE scroller, and it is this one (files-4). The viewer owns the full width
    of the region it is given, so its bar sits at the page's right edge;
    `scrollbar-gutter: stable` reserves that gutter whether or not the bar is
    showing, which is the other half of the swinging fix - the page can no
    longer oscillate between "bar" and "no bar" widths.
  */
  return (
    <div
      ref={containerRef}
      className="w-full h-full bg-surface-sunken overflow-hidden flex flex-col"
      style={{ scrollbarGutter: 'stable' }}
    >
      <div className="relative flex-1 min-h-0 w-full">
        <Worker workerUrl={workerUrl}>
          <Viewer
            fileUrl={pdfUrl}
            plugins={[defaultLayoutPluginInstance]}
            defaultScale={SpecialZoomLevel.PageWidth}
            onDocumentLoad={handleDocumentLoad}
            renderError={renderErrorFallback}
            renderLoader={() => (
              // The whole reading area, centred - not the library's small
              // spinner pinned to the top of the first page slot.
              <div className="absolute inset-0 flex items-center justify-center bg-surface-sunken">
                <Spinner size="lg" label={t('Loading document…')} />
              </div>
            )}
          />
        </Worker>
      </div>
    </div>
  );
};

const SafePdfViewer: React.FC<SafePdfViewerProps> = (props) => {
  const { t } = useTranslation();
  return (
    <PdfRenderBoundary
      resetKey={props.pdfUrl}
      fallback={
        <div className="flex items-center justify-center h-full bg-danger-50 dark:bg-danger-900/20">
          <div className="glass p-6 rounded-lg -danger-200 dark:-danger-800 max-w-sm text-center">
            <p className="text-danger-700 dark:text-danger-300 font-medium mb-2">{t('Unable to display PDF')}</p>
            <p className="text-sm text-danger-600 dark:text-danger-400">{t('This document could not be opened. It may be corrupted or could not be decrypted.')}</p>
            <p className="text-xs text-danger-500 dark:text-danger-400 font-mono break-all mt-2">{props.filename || 'document.pdf'}</p>
          </div>
        </div>
      }
    >
      <PdfViewerBody {...props} />
    </PdfRenderBoundary>
  );
};

export default SafePdfViewer;
